// Worked-area ("coverage") computation: buffer the driven trail by the implement width and clip it
// to the field. Used to paint the covered strip live and report % done — so overlaps and skips are
// visible. Heavy-ish (turf buffer over the whole trail), so callers should throttle re-computation.

import {
  lineString,
  buffer,
  intersect,
  area as turfArea,
  featureCollection,
  polygon as turfPolygon,
} from '@turf/turf';
import type { Feature, MultiPolygon, Polygon } from 'geojson';
import type { LonLat } from './types';

export interface Coverage {
  /** Worked area inside the field, m². */
  workedM2: number;
  /** Worked fraction of the field, 0–100. */
  pct: number;
  /** The covered geometry (clipped to the field) for rendering, or null. */
  geometry: Polygon | MultiPolygon | null;
}

const EMPTY: Coverage = { workedM2: 0, pct: 0, geometry: null };

function closeRing(ring: LonLat[]): LonLat[] {
  const r = ring.slice();
  const a = r[0];
  const b = r[r.length - 1];
  if (a && b && (a[0] !== b[0] || a[1] !== b[1])) r.push(a);
  return r;
}

export function coverage(trail: LonLat[], widthM: number, fieldRing: LonLat[]): Coverage {
  if (trail.length < 2 || !(widthM > 0) || fieldRing.length < 4) return EMPTY;
  const strip = buffer(lineString(trail), widthM / 2, { units: 'meters' });
  if (!strip) return EMPTY;
  const field = turfPolygon([closeRing(fieldRing) as number[][]]);
  const clipped = intersect(featureCollection([strip as Feature<Polygon | MultiPolygon>, field]));
  const worked = clipped ? turfArea(clipped) : 0;
  const fieldArea = turfArea(field);
  const pct = fieldArea > 0 ? Math.min(100, (worked / fieldArea) * 100) : 0;
  return { workedM2: worked, pct, geometry: (clipped ?? strip).geometry as Polygon | MultiPolygon };
}
